import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    TextField,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import { formatBytes } from '../../../utils/format';
import adminApi from '../../../services/adminApi';
import UploadModal from '../../admin/assets/UploadModal';

// Get base URL for images from environment or default
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

export interface Asset {
    _id: string;
    filename: string;
    originalName: string;
    url: string;
    thumbnailUrl: string;
    size: number;
    dimensions: {
        width: number;
        height: number;
    };
    createdAt: string;
    usedIn: {
        couponId: string;
        couponName: string;
    }[];
}

interface AssetSelectionModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (asset: Asset) => void;
    currentAssetId?: string;
    assetType?: 'image' | 'font';
    title?: string;
}

const AssetSelectionModal: React.FC<AssetSelectionModalProps> = ({
    open,
    onClose,
    onSelect,
    currentAssetId,
    assetType,
    title
}) => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (open) {
            fetchAssets();
        }
    }, [open]);

    const fetchAssets = async () => {
        try {
            const response = await adminApi.getAssets();
            setAssets(response.data.data.assets);
        } catch (error) {
            console.error('Failed to fetch assets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImageError = (assetId: string) => {
        setFailedImages(prev => ({ ...prev, [assetId]: true }));
    };

    const getImageUrl = (path: string) => {
        const cleanPath = path.replace(/^\/+/, '');
        const url = `${BASE_URL}/${cleanPath}`;
        return process.env.NODE_ENV === 'development' ? `${url}?t=${new Date().getTime()}` : url;
    };

    const filteredAssets = assets.filter(asset =>
        asset.originalName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleUploadSuccess = (newAsset: Asset) => {
        setAssets([newAsset, ...assets]);
        setUploadModalOpen(false);
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{title || 'Select Image'}</Typography>
                        <Button
                            startIcon={<CloudUploadIcon />}
                            onClick={() => setUploadModalOpen(true)}
                            variant="contained"
                            size="small"
                        >
                            Upload New
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            placeholder="Search assets..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                        />
                    </Box>

                    {loading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredAssets.map((asset) => (
                                <Grid item xs={12} sm={6} md={4} key={asset._id}>
                                    <Card 
                                        sx={{ 
                                            cursor: 'pointer',
                                            border: currentAssetId === asset._id ? 2 : 1,
                                            borderColor: currentAssetId === asset._id ? 'primary.main' : 'divider'
                                        }}
                                        onClick={() => onSelect(asset)}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                height: 140,
                                                backgroundColor: '#f5f5f5',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {failedImages[asset._id] ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    <ImageIcon sx={{ fontSize: 40, mb: 1 }} />
                                                    <Typography variant="caption">Image not available</Typography>
                                                </Box>
                                            ) : (
                                                <CardMedia
                                                    component="img"
                                                    height="140"
                                                    image={getImageUrl(asset.thumbnailUrl)}
                                                    alt={asset.originalName}
                                                    sx={{ objectFit: 'contain' }}
                                                    onError={() => handleImageError(asset._id)}
                                                />
                                            )}
                                        </Box>
                                        <CardContent>
                                            <Typography noWrap title={asset.originalName}>
                                                {asset.originalName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatBytes(asset.size)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {asset.dimensions.width} × {asset.dimensions.height}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </Dialog>

            <UploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onSuccess={handleUploadSuccess}
            />
        </>
    );
};

export default AssetSelectionModal;
